import { auth } from "./firebase.js";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "https://www.gstatic.com/firebasejs/10.13.2/firebase-auth.js";

// Pure logic lives in assets/pure/*.js (loaded as classic scripts by
// index.html) so it can be unit tested directly under Jest. This file is
// DOM wiring only.
const { digitsOnly, isValidMobile, formatFullNumber, maskPhone } = window.TransportOSPhone;
const {
  formatCountdown,
  runCountdown,
  otpFromBoxes,
  isCompleteOtp,
  applyDigit,
  applyBackspace,
  applyPaste,
} = window.TransportOSOtp;
const { friendlyAuthError } = window.TransportOSAuthErrors;
const { t } = window.TransportOSI18n;

const RESEND_SECONDS = 30;

const els = {
  step1: document.getElementById("step-phone"),
  step2: document.getElementById("step-otp"),
  phoneInput: document.getElementById("phone-input"),
  sendBtn: document.getElementById("send-otp-btn"),
  sendError: document.getElementById("phone-error"),
  maskedNumber: document.getElementById("masked-number"),
  changeNumberBtn: document.getElementById("change-number-btn"),
  otpBoxes: Array.from(document.querySelectorAll(".otp-box")),
  verifyBtn: document.getElementById("verify-otp-btn"),
  otpError: document.getElementById("otp-error"),
  countdownRow: document.getElementById("countdown-row"),
  countdownValue: document.getElementById("countdown-value"),
  resendBtn: document.getElementById("resend-btn"),
};

let confirmationResult = null;
let stopCountdownTimer = () => {};

function setError(el, message) {
  el.textContent = message || "";
  el.hidden = !message;
}

// grecaptcha marks the element it rendered into, and clear() does not unmark it —
// rendering into the same element again throws "reCAPTCHA has already been rendered
// in this element". So each verifier gets a throwaway host that reset() removes.
function recaptchaHost() {
  let host = document.getElementById("recaptcha-host");
  if (!host) {
    host = document.createElement("div");
    host.id = "recaptcha-host";
    document.body.appendChild(host);
  }
  return host;
}

function ensureRecaptcha() {
  if (!window.recaptchaVerifier) {
    window.recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaHost(), { size: "invisible" });
  }
  return window.recaptchaVerifier;
}

function resetRecaptcha() {
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (_error) {
      // Already torn down by Firebase; the host removal below is what matters.
    }
    window.recaptchaVerifier = null;
  }
  const host = document.getElementById("recaptcha-host");
  if (host) host.remove();
}

function setBusy(button, busy) {
  button.disabled = busy;
  button.classList.toggle("is-busy", busy);
}

function stopCountdown() {
  stopCountdownTimer();
  stopCountdownTimer = () => {};
}

function startCountdown() {
  stopCountdown();
  els.countdownRow.hidden = false;
  els.resendBtn.hidden = true;
  stopCountdownTimer = runCountdown(RESEND_SECONDS, {
    onTick: (remaining) => {
      els.countdownValue.textContent = formatCountdown(remaining);
    },
    onDone: () => {
      els.countdownRow.hidden = true;
      els.resendBtn.hidden = false;
    },
    setIntervalFn: (fn, ms) => setInterval(fn, ms),
    clearIntervalFn: (id) => clearInterval(id),
  });
}

function focusBox(index) {
  const box = els.otpBoxes[index];
  if (box) box.focus();
}

function clearOtpBoxes() {
  els.otpBoxes.forEach((box) => {
    box.value = "";
    box.classList.remove("is-filled");
  });
}

function currentOtp() {
  return otpFromBoxes(els.otpBoxes.map((b) => b.value));
}

function goToStep2(digits) {
  els.step1.classList.add("is-locked");
  els.phoneInput.disabled = true;
  els.step2.classList.add("is-active");
  els.maskedNumber.textContent = maskPhone(digits);
  clearOtpBoxes();
  setError(els.otpError, "");
  startCountdown();
  focusBox(0);
}

function goToStep1() {
  stopCountdown();
  confirmationResult = null;
  els.step1.classList.remove("is-locked");
  els.phoneInput.disabled = false;
  els.step2.classList.remove("is-active");
  els.countdownRow.hidden = true;
  els.resendBtn.hidden = true;
  clearOtpBoxes();
  setError(els.otpError, "");
  els.phoneInput.focus();
}

async function sendOtp() {
  const digits = digitsOnly(els.phoneInput.value);
  setError(els.sendError, "");
  if (!isValidMobile(digits)) {
    setError(els.sendError, t("auth.error.invalidPhone"));
    return;
  }
  setBusy(els.sendBtn, true);
  try {
    const verifier = ensureRecaptcha();
    confirmationResult = await signInWithPhoneNumber(auth, formatFullNumber(digits), verifier);
    resetRecaptcha();
    goToStep2(digits);
  } catch (err) {
    setError(els.sendError, friendlyAuthError(err));
    resetRecaptcha();
  } finally {
    setBusy(els.sendBtn, false);
  }
}

async function resendOtp() {
  const digits = digitsOnly(els.phoneInput.value);
  setBusy(els.resendBtn, true);
  try {
    resetRecaptcha();
    const verifier = ensureRecaptcha();
    confirmationResult = await signInWithPhoneNumber(auth, formatFullNumber(digits), verifier);
    clearOtpBoxes();
    setError(els.otpError, "");
    startCountdown();
    focusBox(0);
  } catch (err) {
    setError(els.otpError, friendlyAuthError(err));
  } finally {
    setBusy(els.resendBtn, false);
  }
}

async function verifyOtp() {
  const code = currentOtp();
  if (!isCompleteOtp(code) || !confirmationResult) {
    setError(els.otpError, t("auth.error.incompleteCode"));
    return;
  }
  setError(els.otpError, "");
  setBusy(els.verifyBtn, true);
  try {
    await confirmationResult.confirm(code);
    // onAuthStateChanged in main.js takes it from here
  } catch (err) {
    setError(els.otpError, friendlyAuthError(err));
    clearOtpBoxes();
    focusBox(0);
  } finally {
    setBusy(els.verifyBtn, false);
  }
}

function writeBoxes(values) {
  els.otpBoxes.forEach((box, i) => {
    box.value = values[i] || "";
    box.classList.toggle("is-filled", !!box.value);
  });
}

function wireOtpBoxes() {
  els.otpBoxes.forEach((box, index) => {
    box.addEventListener("input", () => {
      const values = els.otpBoxes.map((b) => b.value);
      const result = applyDigit(values, index, box.value);
      writeBoxes(result.boxes);
      focusBox(result.focusIndex);
      if (result.complete) verifyOtp();
    });
    box.addEventListener("keydown", (e) => {
      if (e.key !== "Backspace") return;
      const values = els.otpBoxes.map((b) => b.value);
      const result = applyBackspace(values, index);
      focusBox(result.focusIndex);
    });
    box.addEventListener("paste", (e) => {
      const clipboard = (e.clipboardData || window.clipboardData).getData("text");
      const values = els.otpBoxes.map((b) => b.value);
      const result = applyPaste(values, clipboard);
      if (!result.handled) return;
      e.preventDefault();
      writeBoxes(result.boxes);
      focusBox(result.focusIndex);
      if (result.complete) verifyOtp();
    });
  });
}

export function initAuthUI() {
  wireOtpBoxes();
  els.phoneInput.addEventListener("input", () => {
    els.phoneInput.value = digitsOnly(els.phoneInput.value).slice(0, 10);
  });
  els.phoneInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") sendOtp();
  });
  els.sendBtn.addEventListener("click", sendOtp);
  els.verifyBtn.addEventListener("click", verifyOtp);
  els.changeNumberBtn.addEventListener("click", goToStep1);
  els.resendBtn.addEventListener("click", resendOtp);
}

export function resetSignInForm() {
  els.phoneInput.value = "";
  setError(els.sendError, "");
  goToStep1();
}
